import _ from 'lodash';

import { getDataTypeMeta } from '../../utils/dictionary';
import { columnsOf, isRepeatingGroup } from '../../config/repeatingGroups';
import { formatCount } from '../../utils/counts';
import { confirmDestructive } from '../common/confirmDestructive';

/**
 * The card and element set editors' pre-save question about STORED VALUES
 * (delete audit F9). Removing an element — or detaching an element set, or
 * removing an element from a set — never deletes what items hold under it:
 * the values stay on the items, hidden from every reader, and adding an
 * element with the same name back brings every one of them back. Only the
 * card's "Purge values of removed elements…" removes them, logged and
 * revertable. Retyping an element outside the number ↔ measurement /
 * currency conversion keeps its values as they are.
 *
 * Before such a save the editor asks the server's change-preview (the only
 * party that can count the items) and, when anything holds a value, says
 * so in a danger confirm with Cancel focused — Enter must not be the key
 * that commits it (audit F15).
 */

const VALUE_CODES = new Set(['values_hidden', 'values_kept_unconverted', 'values_converted', 'column_values_hidden']);

// The options of a COLUMN whose change reshapes what its stored values mean
// (keepr-api KPR-675: "Allow multiple" flipped, a measure or a lookup's target
// moved, a file column narrowed to photos). The API refuses the save while
// entries exist (409 type_change_needs_empty { element, column }), which the
// preview answers as its refusal; asking it is how the author learns before
// pressing Save.
// `measures` (KPR-1085): a measure taken away from a measurement column is
// refused while an entry holds a value in it — asked here, so a change that
// only adds one is a preview that finds nothing.
const COLUMN_SHAPE_KEYS = ['allowMultiple', 'measure', 'measures', 'lookupCardId', 'accept'];

/**
 * The columns of a repeating group a save removes, retypes or reshapes, against
 * the saved list. Groups are matched by name, and so are their columns (a
 * renamed column is a new one, as an element is). A group that is new, gone or
 * no longer a group is the element-level rule's; this is the columns' alone.
 *
 * Returns [{ group, column, kind: 'removed' | 'retyped' | 'reshaped' }].
 */
export function destructiveColumnEdits(savedElements = [], nextElements = []) {
	const next = new Map((nextElements || []).filter(el => el?.name).map(el => [el.name, el]));
	const out = [];
	(savedElements || []).forEach(saved => {
		if (!isRepeatingGroup(saved) || !saved.name) return;
		const now = next.get(saved.name);
		if (!isRepeatingGroup(now)) return;
		const nowColumns = new Map(columnsOf(now).filter(col => col?.name).map(col => [col.name, col]));
		columnsOf(saved).forEach(before => {
			if (!before?.name) return;
			const after = nowColumns.get(before.name);
			if (!after) {
				out.push({ group: saved.name, column: before.name, kind: 'removed' });
			} else if ((after.dataType || 'text-small') !== (before.dataType || 'text-small')) {
				out.push({ group: saved.name, column: before.name, kind: 'retyped' });
			} else if (COLUMN_SHAPE_KEYS.some(key => !_.isEqual(before.options?.[key], after.options?.[key]))) {
				out.push({ group: saved.name, column: before.name, kind: 'reshaped' });
			}
		});
	});
	return out;
}

/** Elements a save removes or retypes, against the saved list — does it need the preview? */
export function destructiveElementEdits(savedElements = [], nextElements = []) {
	const next = new Map((nextElements || []).filter(el => el?.name).map(el => [el.name, el]));
	const removed = [];
	const retyped = [];
	(savedElements || []).forEach(el => {
		if (!el?.name) return;
		const now = next.get(el.name);
		if (!now) removed.push(el.name);
		else if ((now.dataType || 'text-small') !== (el.dataType || 'text-small')) retyped.push(el.name);
	});
	return { removed, retyped, columns: destructiveColumnEdits(savedElements, nextElements) };
}

const typeLabel = dataType => getDataTypeMeta(dataType)?.label || dataType || 'the new type';

/**
 * The preview read as the confirm's lines. `labelOf(name)` gives an
 * element's label (a removed element is no longer in the draft, so the
 * caller looks it up in the saved copy); the raw name when it has none.
 *
 * A repeating group's COLUMN changes (KPR-679, keepr-api KPR-675:
 * `column-added`, `column-removed`, `column-retyped`, `column-reshaped`,
 * `column-changed`, `columns-reordered`) are read too. `columnLabelOf(group,
 * column)` gives a column's label (a removed one is no longer in the draft,
 * so the caller looks it up in the saved copy). A removed column of a group
 * that items hold entries in is a value line like a removed element's:
 * `"Ingredient › Notes" has entries on 3 items — they'll be hidden, not
 * deleted.` Every column change is also listed, plainly, in `changes`.
 *
 * Returns { needed, hides, lines: [string], notes: [string], changes:
 * [string] } — `needed` when anything holds a value; `lines` one per element
 * or column; `notes` the server's other side-effect sentences; `changes` the
 * group and column changes, whether or not anything holds a value.
 */
export function readValuesPreview(preview, { labelOf = name => name, columnLabelOf = (group, column) => column } = {}) {
	const changes = Array.isArray(preview?.changes) ? preview.changes : [];
	const sideEffects = Array.isArray(preview?.sideEffects) ? preview.sideEffects : [];
	const named = name => `"${labelOf(name) || name}"`;
	const columnNamed = (group, column) => `"${labelOf(group) || group} › ${columnLabelOf(group, column) || column}"`;
	const lines = [];
	const covered = new Set();
	const coveredColumns = new Set();
	const changeLines = [];
	let hides = false;
	changes.forEach(change => {
		if (typeof change?.kind === 'string' && change.kind.startsWith('column')) {
			const sentence = columnChangeSentence(change, { labelOf, columnLabelOf });
			if (sentence) changeLines.push(sentence);
			const entries = Number(change?.itemsWithEntries) || 0;
			if (change.kind === 'column-removed' && change.destructive && entries > 0) {
				hides = true;
				lines.push(`${columnNamed(change.element, change.column)} has entries on ${formatCount(entries, 'item')} — they'll be hidden, not deleted.`);
				coveredColumns.add(`${change.element}\u0000${change.column}`);
			} else if ((change.kind === 'column-retyped' || change.kind === 'column-reshaped') && entries > 0) {
				// (The API refuses these while entries exist — the preview then
				// answers `wouldApply: false` — so this is only the belt.)
				lines.push(`${columnNamed(change.element, change.column)} has entries on ${formatCount(entries, 'item')} — they're kept as they are and may not read as the new column.`);
				coveredColumns.add(`${change.element}\u0000${change.column}`);
			}
			return;
		}
		const count = Number(change?.itemsWithValues) || 0;
		if (!change?.destructive || count <= 0) return;
		const on = `has values on ${formatCount(count, 'item')}`;
		if (change.kind === 'removed') {
			hides = true;
			lines.push(`${named(change.element)} ${on} — they'll be hidden, not deleted.`);
		} else if (change.kind === 'retyped') {
			// Into or out of a Choice that allows multiple (owner ruling B16):
			// the server's own sentence says what the author chose — "each
			// keeps its first choice; N lose the rest" — so it stands alone.
			// So does an element made an item lookup whose values are cleared
			// (`clear`, KPR-1136): "Those values are emptied…".
			if (change.conversion === 'choice' || change.conversion === 'clear') return;
			if (change.conversion && change.conversion !== 'none') {
				lines.push(`${named(change.element)} ${on} — they'll be converted to ${typeLabel(change.to)}.`);
			} else {
				lines.push(`${named(change.element)} ${on} — they're kept as they are and may not read as ${typeLabel(change.to)}.`);
			}
		} else {
			return;
		}
		covered.add(change.element);
	});
	const notes = [];
	sideEffects.forEach(effect => {
		const message = typeof effect?.message === 'string' ? effect.message.trim() : '';
		if (!message) return;
		if (VALUE_CODES.has(effect.code)) {
			if (effect.code === 'values_hidden' || effect.code === 'column_values_hidden') hides = true;
			// The element's own line already says it, with the count.
			if (effect.code === 'column_values_hidden' && coveredColumns.has(`${effect.element}\u0000${effect.column}`)) return;
			if (effect.code !== 'column_values_hidden' && effect.element && covered.has(effect.element)) return;
			lines.push(message);
			return;
		}
		notes.push(message);
	});
	const needed = lines.length > 0 || sideEffects.some(effect => VALUE_CODES.has(effect?.code));
	return { needed, hides, lines: _.uniq(lines), notes: _.uniq(notes), changes: _.uniq(changeLines) };
}

/**
 * One group or column change as a sentence, in the product's words (a
 * repeating group's elements are COLUMNS). null for a kind this does not know.
 */
function columnChangeSentence(change, { labelOf, columnLabelOf }) {
	const group = `"${labelOf(change.element) || change.element}"`;
	const column = `"${columnLabelOf(change.element, change.column) || change.column}"`;
	switch (change.kind) {
		case 'column-added':
			return `Adds the column ${column} to ${group}${change.to ? ` (${typeLabel(change.to)})` : ''}.`;
		case 'column-removed':
			return `Removes the column ${column} from ${group}.`;
		case 'column-retyped':
			return `Changes ${column} in ${group} from ${typeLabel(change.from)} to ${typeLabel(change.to)}.`;
		case 'column-reshaped':
			return `Changes how ${column} in ${group} is set up${change.option ? ` (${change.option})` : ''}.`;
		case 'column-changed':
			return `Changes ${column} in ${group}${Array.isArray(change.fields) && change.fields.length ? ` (${change.fields.join(', ')})` : ''}.`;
		case 'columns-reordered':
			return `Reorders the columns of ${group}.`;
		default:
			return null;
	}
}

/**
 * Ask. Resolves true to save, false to keep editing. `where` finishes the
 * purge sentence ("the card's Danger zone" / "each card's Danger zone").
 */
export function confirmStoredValues(modal, read, { where = "the card's Danger zone" } = {}) {
	return new Promise(resolve => {
		confirmDestructive(modal, {
			title: read.hides ? 'Hide values on items?' : 'Change how stored values read?',
			width: 560,
			content: (
				<div className="values-preview">
					{read.changes && read.changes.length ? (
						<ul className="values-preview__list values-preview__changes" data-testid="values-preview-changes">
							{read.changes.map(line => <li key={line}>{line}</li>)}
						</ul>
					) : null}
					<ul className="values-preview__list">
						{read.lines.map(line => <li key={line}>{line}</li>)}
					</ul>
					{read.notes.length ? (
						<ul className="values-preview__list">
							{read.notes.map(note => <li key={note}>{note}</li>)}
						</ul>
					) : null}
					{read.hides ? (
						<p>
							Adding an element with the same name back brings them back. A manager can purge them from {where}.
						</p>
					) : null}
				</div>
			),
			okText: read.hides ? 'Save and hide values' : 'Save changes',
			cancelText: 'Keep editing',
			onOk: () => resolve(true),
			onCancel: () => resolve(false),
		});
	});
}

/**
 * When the preview itself could not be asked (the network, a server
 * error): the same question without counts, so a removal is never saved
 * without the sentence about its values.
 */
export function confirmStoredValuesUncounted(modal, { where = "the card's Danger zone" } = {}) {
	return confirmStoredValues(modal, {
		hides: true,
		lines: ['Items may hold values under the elements this save removes or retypes. keepr could not count them right now. Removed elements\' values are hidden, not deleted; retyped ones are kept as they are.'],
		notes: [],
	}, { where });
}

/**
 * The whole pre-save step: ask the preview (`request()`), then ask the
 * person when anything holds a value. Resolves `{ proceed, refusal? }`:
 *
 *   - the preview refused the change (`wouldApply: false`, or a coded 4xx
 *     from its gate) → `refusal` { code, element, message, details }: exactly
 *     what the save would have answered, surfaced as the save's error —
 *     `details` the whole refusal, for a caller that builds more from it (the
 *     "Allow multiple" dialog, owner ruling B16);
 *   - the preview could not be asked (network, 5xx) → the uncounted
 *     question;
 *   - nothing holds a value → proceed without asking.
 */
export async function checkStoredValues({ modal, request, labelOf, columnLabelOf, where }) {
	let preview;
	try {
		preview = await request();
	} catch (error) {
		const status = error?.response?.status;
		const data = error?.response?.data;
		if (status && status < 500 && data && typeof data.message === 'string') {
			return { proceed: false, refusal: { code: data.code || null, element: data.element || null, column: data.column || null, message: data.message, details: data } };
		}
		return { proceed: await confirmStoredValuesUncounted(modal, { where }) };
	}
	if (!preview) return { proceed: true };
	if (preview.wouldApply === false) {
		const refusal = preview.refusal || {};
		return {
			proceed: false,
			refusal: { code: refusal.code || null, element: refusal.element || null, column: refusal.column || null, message: refusal.message || 'The change was refused.', details: refusal },
		};
	}
	const read = readValuesPreview(preview, { labelOf, columnLabelOf });
	if (!read.needed) return { proceed: true };
	return { proceed: await confirmStoredValues(modal, read, { where }) };
}
